/**
 * 對話頁「出歌偏好」狀態（UI v2 步 6：由 `ChatPage.tsx` 抽出，零行為改動）
 *
 *   歌詞語言／語體／風格（＋快速清單）／聲線／演唱性別／版本數／對話模式
 *   ＋ 各自嘅 `localStorage` 記住 ＋ `/api/health` 查聲線有冇配置。
 */
import { useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import type { StyleOption } from '@coeldery/style-picker';
import { GENRES, DEFAULT_QUICK_GENRE_IDS } from '../../genres';
import type { SongLang, SongRegister, VocalGender, ChatMode } from './types';

/** 風格選項（顯示＝中文，入 provider prompt ＝英文；`decisions/026`） */
const STYLE_OPTIONS: (StyleOption & { labelKey: string })[] = [
  { id: 'slow', prompt: 'slow ballad', label: '', labelKey: 'style_slow' },
  { id: 'upbeat', prompt: 'upbeat', label: '', labelKey: 'style_upbeat' },
  { id: 'piano', prompt: 'piano', label: '', labelKey: 'style_piano' },
  { id: 'guitar', prompt: 'acoustic guitar', label: '', labelKey: 'style_guitar' },
  { id: 'drums', prompt: 'drums', label: '', labelKey: 'style_drums' },
  { id: 'nodrums', prompt: 'no drums', label: '', labelKey: 'style_nodrums' },
  { id: 'lofi', prompt: 'lo-fi', label: '', labelKey: 'style_lofi' },
  { id: 'jazz', prompt: 'jazz', label: '', labelKey: 'style_jazz' },
  { id: 'electronic', prompt: 'electronic', label: '', labelKey: 'style_electronic' },
  { id: 'strings', prompt: 'strings', label: '', labelKey: 'style_strings' },
  { id: 'rock', prompt: 'rock', label: '', labelKey: 'style_rock' },
  { id: 'folk', prompt: 'folk', label: '', labelKey: 'style_folk' },
];

export function useChatPrefs({ t, locale, sessionId }: { t: TFunction; locale: string; sessionId: string }) {
  const [songLang, setSongLang] = useState<SongLang>(
    locale === 'en' ? 'en' : locale === 'zh-CN' ? 'zh-CN' : 'zh-HK',
  );
  const langOptions = [
    { code: 'zh-HK', label: t('song_lang_cantonese') },
    { code: 'zh-CN', label: t('song_lang_mandarin') },
    { code: 'en', label: t('song_lang_english') },
  ];

  /**
   * ⭐ **語體（`decisions/030`）**：`cantopop` ＝ 流行曲（市場書面，**預設**）／`spoken` ＝ 口語。
   * ⚠️ 記住上次選擇（`localStorage`）。
   */
  const [songRegister, setSongRegister] = useState<SongRegister>(() => {
    try {
      return localStorage.getItem('cosingery.register') === 'spoken' ? 'spoken' : 'cantopop';
    } catch {
      return 'cantopop';
    }
  });
  const registerOptions = [
    { code: 'cantopop', label: t('register_cantopop') },
    { code: 'spoken', label: t('register_spoken') },
  ];

  /** ⭐ 聲線（預設 **off**；`decisions/047`）＋ 只有真係配置咗才顯示（血淚：換 key 後舊 `vocal_id` 失效） */
  const [useVoice, setUseVoice] = useState<boolean>(() => {
    try {
      return localStorage.getItem('cosingery.useVoice') === 'on';
    } catch {
      return false;
    }
  });
  const [voiceReady, setVoiceReady] = useState(false);

  /** ⭐ 演唱性別（`decisions/051`）*/
  const [vocalGender, setVocalGender] = useState<VocalGender>(() => {
    try {
      const v = localStorage.getItem('cosingery.vocalGender');
      return v === 'male' || v === 'female' ? v : 'auto';
    } catch {
      return 'auto';
    }
  });

  const styleOptions: StyleOption[] = STYLE_OPTIONS.map((o) => ({ id: o.id, label: t(o.labelKey), prompt: o.prompt }));

  /** ⭐ 0017：出幾個版本（1／2；`decisions/041`） */
  const [renderVariants, setRenderVariants] = useState<1 | 2>(() => {
    try {
      return localStorage.getItem('cosingery.renderVariants') === '2' ? 2 : 1;
    } catch {
      return 1;
    }
  });

  /** ⭐ 出歌音樂風格（`decisions/034`）：預設自動（AI 揀）；撳〔自己揀〕才出清單 */
  const [songGenre, setSongGenre] = useState<string | null>(() => {
    try {
      const saved = localStorage.getItem('cosingery.genre');
      return saved && saved.trim() ? saved.trim() : null;
    } catch {
      return null;
    }
  });
  const [genreQuick, setGenreQuick] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('cosingery.genreQuick');
      const parsed = raw ? (JSON.parse(raw) as unknown) : null;
      if (Array.isArray(parsed) && parsed.every((x) => typeof x === 'string') && parsed.length > 0) return parsed as string[];
    } catch {
      /* 私隱模式／壞資料 → 用預設 */
    }
    return [...DEFAULT_QUICK_GENRE_IDS];
  });
  const genreOptions: StyleOption[] = GENRES.map((g) => ({ id: g.id, label: t(g.labelKey), prompt: g.prompt }));
  const pickGenre = (id: string | null) => {
    setSongGenre(id);
    try {
      localStorage.setItem('cosingery.genre', id ?? '');
    } catch {
      /* 寫唔到就當次有效 */
    }
  };
  const toggleQuick = (id: string) => {
    const next = genreQuick.includes(id) ? genreQuick.filter((x) => x !== id) : [...genreQuick, id];
    /** 唔准清空（清空 ＝ 冇快速清單，介面會變返「全部照出」） */
    const safe = next.length > 0 ? next : [...DEFAULT_QUICK_GENRE_IDS];
    setGenreQuick(safe);
    try {
      localStorage.setItem('cosingery.genreQuick', JSON.stringify(safe));
    } catch {
      /* 寫唔到就當次有效 */
    }
  };

  /** ⭐ `decisions/027`：模式（模式只影響人格／語氣；寫歌步驟唔理模式） */
  const [mode, setMode] = useState<ChatMode>('listener');
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`cosingery.mode.${sessionId}`);
      setMode(raw === 'chat' ? 'chat' : 'listener');
    } catch {
      setMode('listener');
    }
  }, [sessionId]);
  useEffect(() => {
    try {
      localStorage.setItem(`cosingery.mode.${sessionId}`, mode);
    } catch {
      /* 私隱模式 */
    }
  }, [mode, sessionId]);

  /** 記住各項選擇 */
  useEffect(() => {
    try {
      localStorage.setItem('cosingery.register', songRegister);
    } catch {
      /* 私隱模式 */
    }
  }, [songRegister]);
  useEffect(() => {
    try {
      localStorage.setItem('cosingery.useVoice', useVoice ? 'on' : 'off');
    } catch {
      /* 私隱模式 */
    }
  }, [useVoice]);
  useEffect(() => {
    try {
      localStorage.setItem('cosingery.vocalGender', vocalGender);
    } catch {
      /* 私隱模式 */
    }
  }, [vocalGender]);
  useEffect(() => {
    try {
      localStorage.setItem('cosingery.renderVariants', String(renderVariants));
    } catch {
      /* 私隱模式 */
    }
  }, [renderVariants]);

  /** 查聲線有冇真係配置（查唔到 → 當冇；保守） */
  useEffect(() => {
    let alive = true;
    fetch('/api/health')
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (alive) setVoiceReady(Boolean((j as { voice?: { configured?: boolean } } | null)?.voice?.configured));
      })
      .catch(() => {
        if (alive) setVoiceReady(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return {
    songLang, setSongLang, langOptions,
    songRegister, setSongRegister, registerOptions,
    useVoice, setUseVoice, voiceReady,
    vocalGender, setVocalGender, styleOptions,
    renderVariants, setRenderVariants,
    songGenre, genreOptions, genreQuick, pickGenre, toggleQuick,
    mode, setMode,
  };
}

